// ═══════════════════════════════════════════════════════════════════════
// Temps › Mon équipe aujourd'hui (agent H ; conception § 14.1).
// Des compteurs qui S'ADDITIONNENT (chacun dans une seule case : présents + congé + mission +
// absence déclarée + repos + à régulariser… = effectif), puis « À regarder », limité aux
// exceptions, avec une action principale par ligne (« Marquer… » ou « Voir »). Le repos n'est pas
// une absence ; la maladie s'affiche « absence déclarée ». Secours : scan et appel nominal selon
// les droits renvoyés par le serveur.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UsersRound, AlertTriangle, Clock, Eye, UserCheck, ScanLine, ClipboardList, CheckCircle2, ListChecks, Inbox } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, BandeauInfo, EtatVide, Squelette } from '../../design/composants';
import { Fenetre } from './reglagesCommun';
import JourneeDetailModal from './JourneeDetailModal';
import { NavEquipe, lireEquipe, messageErreur, codeErreur, jourLong, nomComplet, useDroitsEquipe, FenetreMarquer } from './EquipeCommun';

function Compteur({ valeur, libelle, ton }) {
  return (
    <div className="rh-carte" style={{ marginBottom: 0, padding: '12px 14px', minWidth: 0, borderLeft: ton ? `3px solid var(--sg-${ton})` : undefined }}>
      <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1, color: 'var(--sg-texte)' }}>{valeur}</div>
      <div style={{ fontSize: 13, color: 'var(--sg-texte-3)' }}>{libelle}</div>
    </div>
  );
}
const TONS_CASES = { a_regulariser: 'alerte', attendus: 'info' };

/** « Tous (n) » : la case de chaque collaborateur. */
function FenetreTous({ vue, onFermer, onVoir }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  useEffect(() => { lireEquipe('/aujourdhui/tous', { vue }).then(setD).catch((e) => setErreur(messageErreur(e))); }, [vue]);
  return (
    <Fenetre titre={d ? `Toute l'équipe (${d.total})` : 'Toute l\'équipe'} Icone={ListChecks} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!d && !erreur && <Squelette largeur="50%" />}
      {d && (
        <ul className="rh-liste">
          {d.lignes.map((l) => (
            <li key={l.employe_id}>
              <span style={{ flex: 1 }}>{nomComplet(l)}</span>
              <span className="rh-cellule-secondaire">{l.libelle || 'Non renseigné'}</span>
              <Bouton petit variante="discret" Icone={Eye} onClick={() => onVoir(l, d.date)}>Voir</Bouton>
            </li>
          ))}
        </ul>
      )}
    </Fenetre>
  );
}

export default function EquipeAujourdhuiPage() {
  const droits = useDroitsEquipe();
  const [vue, setVue] = useState('equipe');
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [refus, setRefus] = useState(false);
  const [message, setMessage] = useState('');
  const [fenetre, setFenetre] = useState(null);

  const charger = useCallback(async () => {
    try {
      setErreur(''); setRefus(false);
      setD(await lireEquipe('/aujourdhui', { vue }));
    } catch (e) {
      setD(null);
      if (e?.response?.status === 403) setRefus(codeErreur(e) || true);
      setErreur(messageErreur(e, 'Chargement impossible.'));
    }
  }, [vue]);
  useEffect(() => { charger(); }, [charger]);

  const terminer = (texte) => { setFenetre(null); setMessage(texte); charger(); };
  const secours = droits?.secours || {};

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps' }, { libelle: 'Mon équipe' }]} titre="Mon équipe aujourd'hui"
          sousTitre={d ? `${d.perimetre} · ${jourLong(d.date)}` : 'Qui est là, qui est absent, et ce qui demande votre attention.'}
          actions={droits?.mon_site && droits?.mon_equipe && (
            <div className="rh-vues" role="group" aria-label="Population">
              <button type="button" className="rh-vue" aria-pressed={vue === 'equipe'} onClick={() => setVue('equipe')}>Mon équipe</button>
              <button type="button" className="rh-vue" aria-pressed={vue === 'site'} onClick={() => setVue('site')}>Mon site</button>
            </div>
          )} />
        <NavEquipe actif="aujourdhui" compteur={droits?.compteur_a_traiter} />

        {refus && <EtatVide Icone={UsersRound} titre="Aucune équipe à suivre" texte={erreur} />}
        {!refus && erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
        {!d && !erreur && <Carte><Squelette largeur="70%" /><Squelette largeur="40%" style={{ marginTop: 8 }} /></Carte>}

        {d && (
          <>
            <section aria-label="Compteurs du jour" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
              {d.cases.map((c) => <Compteur key={c.cle} valeur={c.valeur} libelle={c.libelle} ton={c.valeur ? TONS_CASES[c.cle] : undefined} />)}
            </section>
            <p className="rh-form-aide" style={{ margin: '-8px 0 16px' }}>
              {d.compteurs.total} personne{d.compteurs.total > 1 ? 's' : ''} attendue{d.compteurs.total > 1 ? 's' : ''} : chacune compte dans une seule case. Le repos n'est pas une absence.
            </p>

            <Carte titre="À regarder" Icone={AlertTriangle}
              action={<Bouton petit variante="lien" Icone={ListChecks} onClick={() => setFenetre({ type: 'tous' })}>Tous ({d.compteurs.total})</Bouton>}>
              {d.a_regarder.length === 0 ? (
                <EtatVide Icone={CheckCircle2} titre="Rien à signaler" texte="Les journées normales passent seules : aucune exception pour l'instant." />
              ) : (
                <ul className="rh-liste">
                  {d.a_regarder.map((x) => (
                    <li key={`${x.employe_id}-${x.date}-${x.libelle}`}>
                      {x.niveau === 'alerte'
                        ? <AlertTriangle size={16} color="var(--sg-alerte)" aria-label="À traiter" />
                        : <Clock size={16} color="var(--sg-info)" aria-label="Pour information" />}
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontWeight: 500 }}>{nomComplet(x)}</strong>
                        <span className="rh-cellule-secondaire" style={{ marginLeft: 8 }}>{x.libelle}</span>
                      </span>
                      {x.action === 'MARQUER' && vue === 'equipe'
                        ? <Bouton petit variante="primaire" Icone={UserCheck} onClick={() => setFenetre({ type: 'marquer', personne: x, date: x.date })}>Marquer…</Bouton>
                        : <Bouton petit Icone={Eye} onClick={() => setFenetre({ type: 'journee', personne: x, date: x.date })}>Voir</Bouton>}
                    </li>
                  ))}
                </ul>
              )}
            </Carte>

            <Carte>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {(secours.scan || secours.appel) && <span className="rh-cellule-secondaire">Secours :</span>}
                {secours.scan && <Link to="/pointage" className="rh-btn rh-btn-petit" style={{ textDecoration: 'none' }}><ScanLine size={14} aria-hidden="true" />Scanner un code</Link>}
                {secours.appel && <Link to="/pointage" className="rh-btn rh-btn-petit" style={{ textDecoration: 'none' }}><ClipboardList size={14} aria-hidden="true" />Faire l'appel</Link>}
                <span style={{ flex: 1 }} />
                {droits?.a_traiter && (
                  <Link to="/temps/a-traiter" className="rh-btn rh-btn-petit rh-btn-primaire" style={{ textDecoration: 'none' }}>
                    <Inbox size={14} aria-hidden="true" />À traiter{droits.compteur_a_traiter ? ` (${droits.compteur_a_traiter})` : ''}
                  </Link>
                )}
              </div>
            </Carte>
          </>
        )}

        {fenetre?.type === 'tous' && <FenetreTous vue={vue} onFermer={() => setFenetre(null)} onVoir={(p, date) => setFenetre({ type: 'journee', personne: p, date })} />}
        {fenetre?.type === 'marquer' && d && (
          <FenetreMarquer personne={fenetre.personne} date={fenetre.date} options={d.marquer} onFermer={() => setFenetre(null)} onTermine={terminer} />
        )}
        {fenetre?.type === 'journee' && (
          <JourneeDetailModal employeId={fenetre.personne.employe_id} date={fenetre.date} onFermer={() => { setFenetre(null); charger(); }} />
        )}
      </div>
    </Layout>
  );
}
